import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import api from '../../api/api';
import EditableMap from "../../views/EditableMap";
import { getUserId } from "../../utils/auth";
import { useLocation, useNavigate } from "react-router-dom";

const AddFieldWrapper = ({ wizardData, completeStep, onNext, onPrevious, resetAllData, isWizardMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  const [superficie, setSuperficie] = useState(null);
  const [dimensions, setDimensions] = useState(null);
  const [layerFarm, setLayerFarm] = useState([]);
  const location = useLocation();
  
  const [dataField, setDataField] = useState({
    name: "",
    farm_uid: "",
    farmName: "",
    width: "",
    length: "",
    Latitude: "",
    Longitude: ""
  });
  
  const [layer, setLayer] = useState('');
  
  const [coords, setCoords] = useState({
    Latitude: "",
    Longitude: "",
    zoom: "",
    center: [],
    fromAction: false
  });

  // Initialiser les données à partir du wizardData uniquement au montage
  useEffect(() => {
    // Priorité 1: Données du wizard si disponibles
    if (wizardData?.step1?.data) {
      setDataField({
        name: wizardData.step1.data.name || "",
        farm_uid: wizardData.step1.data.farm_uid || "",
        farmName: wizardData.step1.data.farmName || "",
        width: wizardData.step1.data.width || "",
        length: wizardData.step1.data.length || "",
        Latitude: wizardData.step1.data.Latitude || "",
        Longitude: wizardData.step1.data.Longitude || ""
      });
      
      setLayer(wizardData.step1.data.layer || '');
      
      setCoords({
        Latitude: wizardData.step1.data.Latitude || "",
        Longitude: wizardData.step1.data.Longitude || "",
        zoom: "",
        center: [],
        fromAction: false
      });
    } 
    // Priorité 2: Données de la ferme précédemment ajoutée (si wizard step0 est complété)
    else if (wizardData?.step0?.isCompleted && wizardData?.step0?.data) {
      setDataField(prevData => ({
        ...prevData,
        farm_uid: wizardData.step0.data.farmId || "",
        farmName: wizardData.step0.data.farmName || ""
      }));
    }
    // Priorité 3: Données de location.state (navigation standard)
    else if (location.state?.farmId && location.state?.farmName) {
      setDataField(prevData => ({
        ...prevData,
        farm_uid: location.state.farmId,
        farmName: location.state.farmName
      }));
    }
    // Priorité 4: Données de localStorage
    else {
      const lastAddedFarmId = localStorage.getItem('lastAddedFarmId');
      const lastAddedFarmName = localStorage.getItem('lastAddedFarmName');

      if (lastAddedFarmId && lastAddedFarmName) {
        setDataField(prevData => ({
          ...prevData,
          farm_uid: lastAddedFarmId,
          farmName: lastAddedFarmName
        }));
      }
    }
  }, [wizardData, location?.state]);

  useEffect(() => {
    // Vérifier si nous venons d'une soumission récente
    const wasRecentlySubmitted = localStorage.getItem('fieldFormRecentlySubmitted');
    const resetAllForms = localStorage.getItem('resetAllForms');
    
    if (wasRecentlySubmitted || resetAllForms) {
      // Réinitialiser tous les champs du formulaire
      setDataField({
        name: "",
        farm_uid: "",
        farmName: "",
        width: "",
        length: "",
        Latitude: "",
        Longitude: ""
      });
      
      setLayer('');
      setSuperficie(null);
      setDimensions(null);
      setCoords({
        Latitude: "",
        Longitude: "",
        zoom: "",
        center: [],
        fromAction: false
      });
      
      // Supprimer le flag
      localStorage.removeItem('fieldFormRecentlySubmitted');
    }
  }, []);
  
  // Charger les fermes disponibles au début
  useEffect(() => {
    const getLayerFarm = async () => {
      try {
        const response = await api.get('/farm/farms');

        if (response && response.data && response.data.farms) {
          // Transformer les données pour avoir la bonne structure
          const farmsList = response.data.farms.map(farm => ({
            uid: farm.uid,
            name: farm.name,
            id: farm.id
          }));
          setLayerFarm(farmsList);
        }
      } catch (error) {
        console.error("❌ Error loading farms:", error);
      }
    };
    
    getLayerFarm();
  }, []);
  
  // Gérer le retour à l'étape précédente
  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
    }
  };
  
  // Gérer la soumission du formulaire
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Empêcher la double sauvegarde
    if (wizardData?.step1?.isCompleted) {
      onNext();
      return;
    }

    const form = event.currentTarget;
    const hasBoundary = typeof layer === "string" && layer.trim() !== "";
    const isLatLngValid =
      coords.Latitude !== "" &&
      coords.Longitude !== "" &&
      Number.isFinite(Number(coords.Latitude)) &&
      Number.isFinite(Number(coords.Longitude));
    if (!form.checkValidity() || !isLatLngValid || !hasBoundary) {
      event.stopPropagation();
      if (!isLatLngValid || !hasBoundary) {
        swal(t("Error"), t("Please draw a field on the map to set Latitude and Longitude."), "error");
      }
      setValidated(true);
      return;
    }

    const apiData = {
      name: dataField.name,
      farmName: dataField.farmName,
      farm_uid: dataField.farm_uid,
      largeur: dataField.width,
      longueur: dataField.length,
      coordinates: layer,
      Latitude: parseFloat(coords.Latitude).toFixed(4),
      Longitude: parseFloat(coords.Longitude).toFixed(4)
    };

    try {
      const response = await api.post('/field/add-field', apiData);
      if (response.data.type === "success") {
        const fieldFormData = {
          ...dataField,
          fieldId: response.data.field.uid,
          fieldName: response.data.field.name,
          farmId: dataField.farm_uid,
          farmName: dataField.farmName,
          Latitude: coords.Latitude,
          Longitude: coords.Longitude,
          layer: layer
        };
        const isFirstSubmission = !wizardData?.step1?.isCompleted;
        if (isFirstSubmission) {
          localStorage.setItem('fieldFormRecentlySubmitted', 'true');
          swal({
            title: `${t('field_added')}`,
            text: t("Would you like to continue to create a zone (soil info)?"),
            icon: "success",
            buttons: [t("No"), t("Yes")]
          }).then((willContinue) => {
            completeStep(1, fieldFormData);
            if (willContinue) {
              onNext();
            } else {
              const userId = getUserId();
              localStorage.removeItem(`smartFarmWizardData_${userId}`);
              localStorage.setItem('resetAllForms', 'true');
              localStorage.setItem('fieldFormRecentlySubmitted', 'true');
              navigate('/AddField');
            }
          });
        } else {
          completeStep(1, fieldFormData);
          onNext();
        }
      } else {
        swal(t("Error"), t("Failed to add field"), "error");
      }
    } catch (error) {
      swal(t("Error"), t("Failed to add field"), "error");
    }
  };
  
  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit} className="wizard-step-form p-4">
      <h5 className="section-title">{t("field_details")}</h5>
      
      <Row className="mb-4">
        <Col lg={6} md={12}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              {t('name_field')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder= {t("Enter_field_name")}
              value={dataField.name}
              onChange={e => setDataField({ ...dataField, name: e.target.value })}
            />
            <Form.Control.Feedback type="invalid">
              {t('provide_field_name')}
            </Form.Control.Feedback>
<Form.Text className="text-muted">
  {t("field_name_hint")}
</Form.Text>
          </Form.Group>
        </Col>
        
        <Col lg={6} md={12}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              {t('name_farm')} <span className="text-danger">*</span>
            </Form.Label>
            
            {/* 🔧 CORRECTION PRINCIPALE: Remplacement de FarmSelect */}
            {dataField.farmName && dataField.farmName !== "" ? (
              // Si une ferme est déjà sélectionnée, afficher en lecture seule
              <Form.Control
                type="text"
                value={dataField.farmName}
                disabled
                placeholder={t('name_farm')}
                style={{ backgroundColor: "var(--sf-night-surface, #f8f9fa)" }}
              />
            ) : (
              // Sinon, afficher le select pour choisir une ferme
              <Form.Select
                value={dataField.farm_uid}
                onChange={(e) => {
                  const selectedFarm = layerFarm.find(farm => farm.uid === e.target.value);
                  setDataField({
                    ...dataField,
                    farm_uid: e.target.value,
                    farmName: selectedFarm ? selectedFarm.name : ""
                  });
                }}
                required
              >
                <option value="">{t('select_farm')}</option>
                {layerFarm.map((farm) => (
                  <option key={farm.uid} value={farm.uid}>{farm.name}</option>
                ))}
              </Form.Select>
            )}
            
<Form.Text className="text-muted">
  {t("select_farm")}
</Form.Text>

          </Form.Group>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col lg={6} md={6}>
          <Form.Group>
            <Form.Label className="fw-bold">{t('width')} (M)</Form.Label>
            <Form.Control
              type="number"
              placeholder={t("Width_in_meters")}
              value={dataField.width}
              onChange={e => setDataField({ ...dataField, width: e.target.value })}
            />
          </Form.Group>
        </Col>
        
        <Col lg={6} md={6}>
          <Form.Group>
            <Form.Label className="fw-bold">{t('length')} (M)</Form.Label>
            <Form.Control
              type="number"
              placeholder={t("Length_in_meters")}
              value={dataField.length}
              onChange={e => setDataField({ ...dataField, length: e.target.value })}
            />
          </Form.Group>
        </Col>
      </Row>
      
<h5 className="section-title mt-4">{t("field_location")}</h5>
      <div className="map-container mb-4">
        <EditableMap 
          setLayer={setLayer} 
          setCoords={setCoords} 
          initialLayer={layer}
          initialCoords={coords}
          setSuperficie={setSuperficie}
          setDimensions={setDimensions}
        />
      </div>

      <Row>
        <Col lg={6} md={6}>
          <Form.Group className="mb-3">
           <Form.Label className="fw-bold">{t("latitude")}</Form.Label>
            <Form.Control
              disabled
              type="text"
              value={coords.Latitude}
              placeholder={t("Will be set by map")}
            />
          </Form.Group>
        </Col>
        
        <Col lg={6} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{t("longitude")}</Form.Label>
            <Form.Control
              disabled
              type="text"
              value={coords.Longitude}
              placeholder={t("Will be set by map")}
            />
          </Form.Group>
        </Col>
      </Row>
      {superficie !== null && (
  <div
    className="mt-3 p-3 rounded"
    style={{ backgroundColor: "var(--sf-night-inset, #e3f2f7)", border: "1px solid var(--sf-night-brand, #00738e)" }}
  >
    <strong style={{ color: "var(--sf-night-brand, #00738e)" }}>{t('calculated_area')}</strong>{' '}
    <span>
      {superficie.toLocaleString('fr-FR')} m²
      {' '}={' '}
      <strong>{(superficie / 10000).toFixed(4)} ha</strong>
    </span>
    <div className="text-muted small mt-1">
      {dimensions?.radius ? (
        <>
          {t("radius")}: {dimensions.radius} m — {t("diameter")}: {dimensions.width} m
        </>
      ) : (
        <>
          S = Largeur × Longueur ≈ {Math.round(Math.sqrt(superficie))} m × {Math.round(Math.sqrt(superficie))} m
        </>
      )}
    </div>
  </div>
)}
      
      <div className="d-flex justify-content-between mt-4">
        {/* Afficher le bouton Précédent uniquement si on vient d'AddFarm */}
        {isWizardMode && wizardData?.step0?.isCompleted && (
          <Button variant="outline-secondary" onClick={handlePrevious} className="bg-white">
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            {t('previous')}
          </Button>
        )}
        
        {/* Déplacer le bouton Submit/Next à droite */}
        <div className={isWizardMode && wizardData?.step0?.isCompleted ? "" : "ms-auto"}>
          <Button type="submit" variant="primary" className="px-4 py-2">
            {wizardData?.step1?.isCompleted ? (
              <>
                {t('next')} <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
              </>
            ) : (
              t('submit')
            )}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default AddFieldWrapper;
